import { Box, Card, Stack, Typography } from "@mui/material";
import { CombinedWarehouseOrderItemDto, FulfillmentStatus, OrderLineItemDto } from "@lib/ShiOneClient";
import {
  groupItemsByShipment,
  type ShipmentGroup,
} from "../../order-detail-panel/ShipmentMeta";
import { OrderDrawerIdentifier } from "@features/orders-features/components/order-drawer";
import { useEffect, useMemo } from "react";
import { ShipmentProductTable } from "./content/ShipmentProductTable";
import { ShipmentStatusHeader } from "./content/ShipmentStatusHeader";
import { usePrefetchShipmentTracking } from "@features/orders-features/hooks/useGetShipmentTracking";
import { ShipmentMap } from "./ShipmentMap";
import { CustomerExperienceShipmentMapProvider } from "@features/maps-feature/CustomerExperienceMapProviders";
import { useShipmentMapContext } from "shared-ui/src/features/maps-feature/ShipmentMapProvider";

interface OrderDrawerShipmentsProps {
  orderId: OrderDrawerIdentifier;
  shipments: Record<string, OrderLineItemDto[]>;
  onProductClick?: (item: OrderLineItemDto) => void;
  onTrackPackage?: (group: ShipmentGroup, status: string) => void;
  currencyCode?: string | null;
  orderDate?: Date | string | null;
  /** Address lines from the order's ship-to used to place the green destination marker. */
  shipToAddress?: string[] | null;
  /** Warehouse pick-ticket items for this order (config orders only). */
  myWarehouseOrders?: CombinedWarehouseOrderItemDto[] | null;
  /** Returns true when the given product descriptor/class is on backorder. */
  isBackorder?: (
    productDescriptor: string | undefined,
    productClass: string | undefined,
  ) => boolean;
}

/**
 * Inner component that consumes ShipmentMapContext.
 * Separated to allow context hook usage within provider.
 */
function ShipmentMapContent({ shipToAddress }: { shipToAddress?: string[] | null }) {
  const { data: mapData, isLoading, isError } = useShipmentMapContext();
  return (
    <ShipmentMap
      mapData={mapData}
      isLoading={isLoading}
      isError={isError}
      shipToAddress={shipToAddress}
    />
  );
}

export interface ShipmentCard {
  key: string;
  sectionTitle: string;
  shipmentLabel?: string;
  isShipping: boolean;
  items: OrderLineItemDto[];
  group?: ShipmentGroup;
}

// orderLineProducts keys are already the display status ("Shipped",
// "Delivered", "Email Delivery", ...). Only the physical fulfillment states
// render as shipments; other statuses (e.g. "Email Delivery") are excluded.
function isShippingSectionTitle(title: string): boolean {
  return (
    title === FulfillmentStatus.Shipped ||
    title === FulfillmentStatus.Delivered
  );
}

function isDeliveredSectionTitle(title: string): boolean {
  return title === FulfillmentStatus.Delivered;
}


/**
 * Flattens the grouped shipment dictionary into one entry per rendered card.
 * Shipping sections are split by unique shipment (tracking) so each shipment
 * renders as its own card and is counted individually.
 */
export function buildShipmentCards(
  shipments: Record<string, OrderLineItemDto[]>,
): ShipmentCard[] {
  const cards = Object.entries(shipments)
    .filter(
      ([key, items]) =>
        items.length > 0 && key.trim().replace(/[^a-zA-Z]/g, "").length > 0,
    )
    .flatMap(([key, items]): ShipmentCard[] => {
      const sectionTitle = key;
      const isShipping = isShippingSectionTitle(sectionTitle);

      if (isShipping) {
        return groupItemsByShipment(items).map((group) => ({
          key: `${key}:${group.key}`,
          sectionTitle,
          isShipping: true,
          items: group.items,
          group,
        }));
      }

      return [{ key, sectionTitle, isShipping: false, items }];
    });

  // Latest shipments first; cards still processing (non-shipping) go last.
  const shippedTime = (card: ShipmentCard) =>
    card.group?.shippedOn ? new Date(card.group.shippedOn).getTime() : 0;
  cards.sort((a, b) => {
    if (a.isShipping !== b.isShipping) {
      return a.isShipping ? -1 : 1;
    }
    return a.isShipping ? shippedTime(b) - shippedTime(a) : 0;
  });

  // Number each physical shipment ("Shipment N of X"). Only shipped/delivered
  // cards count toward X; a single shipment is never labelled "1 of 1".
  const shipmentTotal = cards.filter((card) => card.isShipping).length;
  if (shipmentTotal <= 1) {
    return cards;
  }

  let shipmentNumber = 0;
  return cards.map((card) => {
    if (!card.isShipping) {
      return card;
    }
    shipmentNumber += 1;
    return {
      ...card,
      shipmentLabel: `Shipment ${shipmentNumber} of ${shipmentTotal}`,
    };
  });
}

export function OrderDrawerShipments({
  orderId,
  shipments,
  onProductClick,
  onTrackPackage,
  currencyCode,
  orderDate,
  shipToAddress,
  myWarehouseOrders,
  isBackorder,
}: Readonly<OrderDrawerShipmentsProps>) {
  const cards = useMemo(() => buildShipmentCards(shipments), [shipments]);

  // Warm the tracking cache for every shipment when the drawer opens so the
  // "Track Package" drawer resolves instantly on click.
  const prefetchTracking = usePrefetchShipmentTracking();
  useEffect(() => {
    cards.forEach((card) => {
      if (card.group?.trackingNumber) {
        prefetchTracking(orderId.biOrderId, card.group.trackingNumber);
      }
    });
  }, [cards, orderId.biOrderId, prefetchTracking]);

  if (cards.length === 0) {
    return (
      <Box         sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          px: 2,
          py: 2,
      }}>
        <Typography variant="body2" color="text.secondary">
          No shipments found.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {cards.map((card) => {
        const trackGroup = card.group;
        const handleTrack =
          onTrackPackage && trackGroup
            ? () => onTrackPackage(trackGroup, card.sectionTitle)
            : undefined;

        return (
          <Card
            key={card.key}
            variant="outlined"
            sx={{ p: 2, borderRadius: 1 }}
          >
            <ShipmentStatusHeader
              sectionTitle={card.sectionTitle}
              shipmentLabel={card.shipmentLabel}
              group={card.group}
              orderDate={orderDate}
              onTrackPackage={handleTrack}
            />
            {card.isShipping && !isDeliveredSectionTitle(card.sectionTitle) && card.group && (
              <Box sx={{ mt: 2, height: "100%" }}>
                <CustomerExperienceShipmentMapProvider
                  biOrderId={orderId.biOrderId}
                  trackingNumber={card.group.trackingNumber}
                  lastSeenLocation={card.group.items[0]?.lastSeenLocation}
                >
                  <ShipmentMapContent shipToAddress={shipToAddress} />
                </CustomerExperienceShipmentMapProvider>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
                  <ShipmentProductTable
                      items={card.items}
                      currencyCode={currencyCode}
                      onProductClick={onProductClick}
                      biOrderId={orderId.biOrderId}
                      myWarehouseOrders={
                        !card.isShipping &&
                        card.sectionTitle.toLowerCase() === "processing"
                          ? myWarehouseOrders
                          : undefined
                      }
                      isBackorder={
                        !card.isShipping &&
                        card.sectionTitle.toLowerCase() === "processing"
                          ? isBackorder
                          : undefined
                      }
                  />
              </Box>
          </Card>
        );
      })}
    </Stack>
  );
}
